import React, { useEffect, useRef } from "react";

interface PersistentPageStackProps {
  activePath: string;
  openPaths: string[];
  children: React.ReactNode;
}

/**
 * Keep every open ERP page mounted until its tab is explicitly closed.
 *
 * React Router normally unmounts the previous page during navigation, which
 * destroys unsaved form state.  The tab bar already models the pages the user
 * considers open, so this stack uses that same lifecycle: switching tabs only
 * hides a page; closing its tab removes and unmounts it.
 */
const PersistentPageStack: React.FC<PersistentPageStackProps> = ({
  activePath,
  openPaths,
  children,
}) => {
  const pageCache = useRef<Map<string, React.ReactNode>>(new Map());

  if (!pageCache.current.has(activePath)) {
    pageCache.current.set(activePath, children);
  }

  const renderPaths = Array.from(new Set([...openPaths, activePath])).filter(path =>
    pageCache.current.has(path)
  );

  useEffect(() => {
    const retainedPaths = new Set([...openPaths, activePath]);
    for (const path of Array.from(pageCache.current.keys())) {
      if (!retainedPaths.has(path)) pageCache.current.delete(path);
    }
  }, [activePath, openPaths]);

  return (
    <>
      {renderPaths.map(path => {
        const isActive = path === activePath;
        return (
          <div
            key={path}
            data-erp-page-path={path}
            data-erp-page-state={isActive ? "active" : "cached"}
            aria-hidden={!isActive}
            style={{ display: isActive ? "block" : "none", minHeight: "100%" }}
          >
            {pageCache.current.get(path)}
          </div>
        );
      })}
    </>
  );
};

export default PersistentPageStack;
